import { useInfiniteQuery } from "@tanstack/react-query";
import api from "../../../api";
import { fromJson } from "../../../domain-adapters/LectureStudentAnalytics";
import { Lecture } from "../../../domains/Lecture";
import {
  LectureStudentAnalytics,
  OrderType,
} from "../../../domains/LectureStudentAnalytics";
import { createError, HTTPErrors } from "../../../errors";
import { getNextPageParam } from "../../../helpers/ReactQueryHelper";
import ApiResponse from "../../../interfaces/ApiResponse";
import SectionInterface from "../../../interfaces/SectionInterface";
import { SearchCondition } from "../types";

type ResponseData = ApiResponse<LectureStudentAnalytics>;

type Props = {
  section: SectionInterface;
  lecture: Lecture;
  searchCondition: SearchCondition;
};
export const useFetchAnalyticsLectureStudent = ({
  lecture,
  section,
  searchCondition,
}: Props) => {
  return useInfiniteQuery<ResponseData, HTTPErrors>({
    queryKey: cachekeyOf({ section, searchCondition, lecture }),
    queryFn: async ({ pageParam }) => {
      const res = await api.interruptGet(urlOf({ section, lecture }), {
        query: {
          page: pageParam,
          lecture_session_id: searchCondition.lectureSessionId,
          pagination_dir: searchCondition.paginationDir,
          order: convertOrderType(searchCondition.order),
          order_dir: searchCondition.orderDir,
        },
      });
      if (res.ok) {
        const json = await res.json();
        return {
          meta: json.lectureStudentsAnalyticsTable.meta,
          data: fromJson(json.lectureStudentsAnalyticsTable.data),
        };
      }
      throw await createError(res);
    },
    initialPageParam: 1,
    getNextPageParam,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
};

const cachekeyOf = ({ section, lecture, searchCondition }: Props) => [
  "analyticsLectureStudent",
  section.id,
  lecture.id,
  searchCondition,
];

const urlOf = ({ section, lecture }: Pick<Props, "section" | "lecture">) =>
  `/api/v1/sections/${section.id}/analytics/lectures/${lecture.id}/students`;

const convertOrderType = (order?: OrderType) => {
  if (!order) {
    return null;
  }
  return order === "totalScore"
    ? "total_score"
    : "with_transferred_total_score";
};
